/**
 * Auth Service - Wraps MSAL operations
 * Handles token acquisition with fallback strategies
 */

import {
  msalInstance,
  loginRequest,
  licensingApiRequest,
  initializeMsal,
} from '../config/authConfig';
import type { AccountInfo } from '@azure/msal-browser';
import {
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { emitSessionExpired } from './authEvents';

/**
 * Thrown when the MSAL session has expired or the silent token refresh timed out
 * (e.g. monitor_window_timeout). Callers should show a "Please sign in again" prompt.
 *
 * Every construction also emits on the authEvents bus, so SessionGate can force a
 * clean sign-out + redirect regardless of which feature happened to trigger this —
 * without every caller needing to remember to handle it.
 */
export class AuthSessionExpiredError extends Error {
  constructor(
    message = 'Your session has expired. Please sign in again using your Microsoft account.',
  ) {
    super(message);
    this.name = 'AuthSessionExpiredError';
    emitSessionExpired(message);
  }
}

// Guard against concurrent token popup calls (React Strict Mode double-mount,
// or multiple useLicense() hooks mounting simultaneously).
let _licensingTokenInFlight: Promise<string> | null = null;

/**
 * Get access token for Injio NG Licensing API with silent → popup fallback.
 * Returns access token with audience: api://d574b8a7-9ae5-4a61-bde4-e2e3c92a6ebd
 * Use this ONLY for calling the Licensing API (verify-license endpoint).
 *
 * IMPORTANT: Never falls back to acquireTokenRedirect — that navigates the entire
 * app away (full page reload → infinite loop during onboarding for new tenants).
 * If popup is blocked or MSAL is already handling an interaction, throws so callers
 * can handle gracefully.
 */
export async function getLicensingApiToken(): Promise<string> {
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user');
  }

  try {
    // Silent token acquisition — fast path, no UI
    const response = await msalInstance.acquireTokenSilent({
      scopes: licensingApiRequest.scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error: any) {
    // interaction_in_progress: another popup/redirect is already running.
    // Reuse the in-flight promise if one exists so we don't start a second interaction.
    if (error?.errorCode === 'interaction_in_progress') {
      if (_licensingTokenInFlight) return _licensingTokenInFlight;
      throw new Error(
        'MSAL interaction already in progress. Retry after current interaction completes',
      );
    }

    // Consent or re-authentication required → popup only (never redirect)
    if (error instanceof InteractionRequiredAuthError) {
      console.log('[authService] Interaction required - showing consent popup');
      if (_licensingTokenInFlight) return _licensingTokenInFlight;

      _licensingTokenInFlight = msalInstance
        .acquireTokenPopup(licensingApiRequest)
        .then((r) => r.accessToken)
        .finally(() => {
          _licensingTokenInFlight = null;
        });

      return _licensingTokenInFlight;
    }

    // Other errors (network, server, etc.) — rethrow, do NOT redirect
    console.error('[authService] Token acquisition failed:', error);
    throw error;
  }
}

/**
 * Get ID token for Customer Portal backend API (authorize-user, onboarding, etc.)
 * Returns ID token which contains user claims (oid, tid, email, name)
 * Use this for your own Azure Functions backend endpoints
 */
export async function getIdToken(): Promise<string> {
  // Ensure MSAL is initialized before attempting to acquire tokens
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new Error('No authenticated user');
  }

  try {
    // Request token with basic scopes (User.Read)
    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: accounts[0],
    });
    // Return ID token which contains user identity claims
    return response.idToken;
  } catch (error) {
    // Check if interaction is required
    if (error instanceof InteractionRequiredAuthError) {
      console.log('[authService] Interaction required for ID token');
      try {
        const response = await msalInstance.acquireTokenPopup(loginRequest);
        return response.idToken;
      } catch (popupError) {
        console.error('[authService] Popup failed:', popupError);
        await msalInstance.acquireTokenRedirect(loginRequest);
        throw new Error('Redirecting for authentication');
      }
    }

    console.error('[authService] ID token acquisition failed:', error);
    throw error;
  }
}

/**
 * Scopes specifically for Microsoft Graph API access (SharePoint operations)
 * Kept separate from loginRequest.scopes to ensure the correct token audience
 */
const graphApiRequest = {
  scopes: [
    'https://graph.microsoft.com/Sites.Read.All',
    'https://graph.microsoft.com/Sites.ReadWrite.All',
  ],
};

export async function getSPGraphAccessToken(): Promise<string> {
  // Ensure MSAL is initialized before attempting to acquire tokens
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new AuthSessionExpiredError();
  }

  try {
    // Request token scoped explicitly to Graph API (ensures correct audience)
    const response = await msalInstance.acquireTokenSilent({
      scopes: graphApiRequest.scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    const isInteractionRequired = error instanceof InteractionRequiredAuthError;
    const isTimeoutOrBrowserError = error instanceof BrowserAuthError;

    if (isInteractionRequired || isTimeoutOrBrowserError) {
      console.warn(
        '[authService] SP Graph token: interaction/timeout — attempting popup...',
      );
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...graphApiRequest,
          prompt: 'select_account',
        });
        return response.accessToken;
      } catch (popupError: any) {
        console.error('[authService] SP Graph popup failed:', popupError);
        throw new AuthSessionExpiredError();
      }
    }

    console.error('[authService] SP Graph token acquisition failed:', error);
    throw error;
  }
}

/**
 * Silently try to get a SharePoint Graph access token.
 * Returns null instead of throwing — safe to call before consent is granted.
 * Use this for optional Graph calls (e.g. bootstrap-status during onboarding).
 */
export async function tryGetSPGraphAccessToken(): Promise<string | null> {
  try {
    await initializeMsal();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const response = await msalInstance.acquireTokenSilent({
      scopes: graphApiRequest.scopes,
      account: accounts[0],
    });
    return response.accessToken || null;
  } catch {
    // Not yet consented or token unavailable — return null gracefully
    console.warn(
      '[authService] Could not silently acquire Graph token (not yet consented or not available)',
    );
    return null;
  }
}

/**
 * Build MSAL scope request for the classic SharePoint REST API (`_api/web/...`),
 * scoped to a specific tenant's SharePoint hostname. Unlike Microsoft Graph, whose
 * OAuth resource is a single fixed audience (graph.microsoft.com) regardless of
 * which tenant's data is being read, the SharePoint REST API's resource is the
 * tenant's own hostname — so the caller must resolve the hostname first (see
 * getSharePointRootHostname() in sharePointService.ts) before requesting this token.
 */
function spRestApiRequest(hostname: string) {
  return { scopes: [`https://${hostname}/AllSites.Read`] };
}

/**
 * Get an access token for the classic SharePoint REST API, scoped to the given
 * SharePoint tenant hostname (e.g. "contoso.sharepoint.com"). Used to check native
 * SharePoint group membership, which Microsoft Graph does not expose directly.
 */
export async function getSPRestAccessToken(hostname: string): Promise<string> {
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new AuthSessionExpiredError();
  }

  const request = spRestApiRequest(hostname);

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: request.scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (error) {
    const isInteractionRequired = error instanceof InteractionRequiredAuthError;
    const isTimeoutOrBrowserError = error instanceof BrowserAuthError;

    if (isInteractionRequired || isTimeoutOrBrowserError) {
      console.warn('[authService] SP REST token: interaction/timeout — attempting popup...');
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...request,
          prompt: 'select_account',
        });
        return response.accessToken;
      } catch (popupError) {
        console.error('[authService] SP REST popup failed:', popupError);
        throw new AuthSessionExpiredError();
      }
    }

    console.error('[authService] SP REST token acquisition failed:', error);
    throw error;
  }
}

/**
 * Silently try to get a SharePoint REST API token for the given hostname.
 * Returns null instead of throwing — safe to call before consent is granted,
 * mirroring tryGetSPGraphAccessToken().
 */
export async function trySPRestAccessToken(hostname: string): Promise<string | null> {
  try {
    await initializeMsal();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const response = await msalInstance.acquireTokenSilent({
      scopes: spRestApiRequest(hostname).scopes,
      account: accounts[0],
    });
    return response.accessToken || null;
  } catch {
    console.warn(
      '[authService] Could not silently acquire SP REST token (not yet consented or not available)',
    );
    return null;
  }
}

/**
 * @deprecated Use getLicensingApiToken() for Licensing API or getIdToken() for backend API
 * This function maintained for backward compatibility
 */
export async function getAccessToken(): Promise<string> {
  console.warn(
    '[authService] getAccessToken() is deprecated. Use getLicensingApiToken() or getIdToken() instead.',
  );
  return getLicensingApiToken();
}

/**
 * Get current authenticated account
 */
export function getCurrentAccount(): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0 ? accounts[0] : null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return msalInstance.getAllAccounts().length > 0;
}

/**
 * Login with popup
 */
export async function loginPopup(): Promise<AccountInfo> {
  await initializeMsal();
  const response = await msalInstance.loginPopup(loginRequest);
  return response.account;
}

/**
 * Login with redirect
 *
 * MSAL's navigateToLoginRequestUrl defaults to true, which returns the user to
 * whichever page called this after AAD auth completes — fine from LandingPage
 * (which has the handleRedirectPromise → route-onward logic), but a trap from
 * any other page (e.g. /session-expired): the user actually signs in, MSAL
 * lands them right back on that same page, and nothing there checks auth state
 * or routes onward, so it looks like sign-in silently failed/looped. Forcing
 * redirectStartPage to the app root ensures every caller returns to
 * LandingPage's redirect handler, regardless of where loginRedirect() was
 * invoked from.
 */
export async function loginRedirect(): Promise<void> {
  await initializeMsal();
  await msalInstance.loginRedirect({
    ...loginRequest,
    redirectStartPage: `${window.location.origin}/`,
  });
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  await initializeMsal();
  await msalInstance.logoutPopup();
}

/**
 * Get Access Token for Microsoft Graph
 * Used for SharePoint operations via Graph API
 */
export async function getGraphAccessToken(): Promise<string> {
  await initializeMsal();

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    throw new AuthSessionExpiredError();
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: graphApiRequest.scopes,
      account: accounts[0],
    });
    // console.log('[authService] Graph Token Acquired. Scopes:', response.scopes);
    return response.accessToken;
  } catch (error) {
    const isInteractionRequired = error instanceof InteractionRequiredAuthError;
    const isTimeoutOrBrowserError = error instanceof BrowserAuthError;

    if (isInteractionRequired || isTimeoutOrBrowserError) {
      const reason = isTimeoutOrBrowserError
        ? `Silent token refresh timed out (${(error as BrowserAuthError).errorCode}).`
        : 'Consent or re-authentication required.';
      console.warn(`[authService] ${reason} Attempting popup re-login...`);

      try {
        const response = await msalInstance.acquireTokenPopup({
          ...graphApiRequest,
          prompt: 'select_account', // force account picker so user can switch if needed
        });
        return response.accessToken;
      } catch (popupError: any) {
        // popup_window_error, user_cancelled, or another browser error
        console.error('[authService] Re-login popup failed:', popupError);
        throw new AuthSessionExpiredError();
      }
    }

    console.error('[authService] Graph token acquisition failed:', error);
    throw error;
  }
}
